import { useCallback, useRef, useState } from 'react';

import {
  type EmbeddedPaymentElementConfiguration,
  type IntentConfiguration,
} from '@stripe/stripe-react-native';

import { useToggleState } from '@sg/garnish';
import { OrderPaymentStatus } from '@sg/graphql-schema';
import { OrderTypeValues } from '@sg/shared-be-features-ordering';
import { PaymentState } from '@sg/shared-fe-features-ordering';

import { PaymentMethod, storePendingPaymentInfo } from '@order/features-ordering';
import {
  currencyCode,
  getElementConfig,
  paymentMethodTypes,
  paymentMethodTypesWithCashApp,
  setupFutureUsage,
  useNotifyStripeCancel,
  useNotifyStripeError,
  useStripeConfirmHandler,
  useStripeConfirmPayment,
  useStripePaymentElement,
} from '@order/integrations-stripe';
import { useTelemetry } from '@order/Telemetry';

import { type GiftCardCheckoutFormReturn } from '../../form';
import { useGiftCardPaymentIntent } from '../../hooks';
import { useGiftCardPaymentInfo } from '../useGiftCardPaymentInfo';

// ─────────────────────────────────────────────────────────────────────────────

export const useGiftCardStripeIntegration = (props: UseGiftCardStripeIntegrationProps) => {
  const {
    amount,
    form,
    clearPaymentState,
    failPayment,
    paymentMethods,
    requestFormSubmission,
    startPolling,
    createPayment,
    cancelPayment,
  } = props;

  // ─── State ─────────────────────────────────────────────────────────────────

  const {
    value: isConfirmingPayment,
    toggleOn: startStripeConfirmation,
    toggleOff: finishStripeConfirmation,
  } = useToggleState(false);

  const isAsyncPaymentsEnabled = Boolean(startPolling);
  const [orderId, setOrderId] = useState('');
  const assetId = form.getValues('assetId');

  const { stripeCustomerId, stripeCustomerKey } = useGiftCardPaymentInfo();

  // ─── Telemetry ─────────────────────────────────────────────────────────────

  const { track } = useTelemetry();

  const notifyStripeError = useNotifyStripeError();
  const notifyStripeCancel = useNotifyStripeCancel();
  const trackStripeUsage = useCallback(
    (payload: unknown) => {
      track('gift-card.stripe-usage', payload);
    },
    [track],
  );

  // ─── Payment Intent Creation ───────────────────────────────────────────────

  const createGiftCardPaymentIntent = useGiftCardPaymentIntent();
  const createPaymentIntent = useCallback(
    async (
      paymentMethodId: string,
      _paymentMethodType: string,
      shouldSetupFutureUsage?: boolean,
    ) => {
      return await createGiftCardPaymentIntent({
        shouldSetupFutureUsage,
        assetId,
        paymentMethodId,
        balance: form.getValues('amount'),
        deliveryDateTime: form.getValues('deliveryDateTime'),
        deliveryMethods: [form.getValues('deliveryMethod')],
        isAsap: form.getValues('deliveryDateOption') === 'NOW',
        message: form.getValues('message'),
        quantity: form.getValues('quantity'),
        recipientEmail: form.getValues('recipientEmail'),
        recipientName: form.getValues('recipientName'),
        senderName: form.getValues('senderName'),
        utcOffset: form.getValues('utcOffset') ?? 0,
      });
    },
    [createGiftCardPaymentIntent, assetId, form],
  );

  const onPaymentIntentCreated = useCallback(
    async (paymentIntent: { paymentIntentId: string; orderId: string }) => {
      const { paymentIntentId, orderId } = paymentIntent;

      // Stores the resulting order id for after the confirmation.
      setOrderId(orderId);

      // Stores the resulting payment intent id for after the confirmation.
      createPayment?.({
        paymentIntentId,
        paymentStateExpiration: Date.now(),
        paymentStateStatus: OrderPaymentStatus.Created,
        paymentStateError: undefined,
      });

      // We need to set the order id in local storage after payment intent creation.
      // This will be used to show gift card fulfillment feedback from the home screen.
      await storePendingPaymentInfo(orderId, OrderTypeValues.GiftCardOrder, assetId);
    },
    [createPayment, assetId],
  );

  // ─── Element Intent Config ─────────────────────────────────────────────────

  const { stripeConfirmHandler } = useStripeConfirmHandler({
    isAsyncPaymentsEnabled,
    trackStripeUsage,
    createPaymentIntent,
    onOrderReadyForPayment: requestFormSubmission,
    onPaymentIntentCreated,
  });

  const stripeElementConfigRef = useRef<EmbeddedPaymentElementConfiguration>(
    getElementConfig({
      returnURL: `sweetgreen-order://gift/checkout/${assetId}`,
      stripeCustomerId,
      stripeCustomerKey,
    }),
  );

  const stripeIntentConfigRef = useRef<IntentConfiguration>({
    paymentMethodTypes: isAsyncPaymentsEnabled ? paymentMethodTypesWithCashApp : paymentMethodTypes,
    mode: isAsyncPaymentsEnabled
      ? { amount, currencyCode }
      : { amount, currencyCode, setupFutureUsage },
    confirmHandler: stripeConfirmHandler,
  });

  // ─── Embedded Payment Element ──────────────────────────────────────────────

  const { embeddedPaymentElementView, selectedPaymentMethod, confirmStripe, isUpdatingTotal } =
    useStripePaymentElement({
      isAsyncPaymentsEnabled,
      total: amount,
      paymentMethods,
      stripeIntentConfigRef,
      stripeElementConfigRef,
    });

  // ─── Payment Confirmation ──────────────────────────────────────────────────

  const onStartStripeConfirmation = useCallback(() => {
    startStripeConfirmation();
    clearPaymentState?.();
  }, [startStripeConfirmation, clearPaymentState]);

  const onOrderReadyForPayment = useCallback(
    (paymentMethodId?: string) => {
      if (paymentMethodId) void requestFormSubmission(paymentMethodId);
    },
    [requestFormSubmission],
  );

  const onIntentConfirmed = useCallback(() => {
    if (orderId) startPolling?.(orderId);
  }, [orderId, startPolling]);

  const onPaymentFailure = useCallback(
    (error: string) => {
      notifyStripeError(error);
      failPayment();
    },
    [failPayment, notifyStripeError],
  );

  const onPaymentCanceled = useCallback(() => {
    notifyStripeCancel();
    cancelPayment?.();
  }, [cancelPayment, notifyStripeCancel]);

  const { confirmPayment } = useStripeConfirmPayment({
    isAsyncPaymentsEnabled,
    orderId,
    orderType: OrderTypeValues.GiftCardOrder,
    total: amount,
    paymentMethods,
    selectedPaymentMethod,
    trackStripeUsage,
    startStripeConfirmation: onStartStripeConfirmation,
    finishStripeConfirmation,
    confirmStripe,
    onOrderReadyForPayment,
    onIntentConfirmed,
    onPaymentFailure,
    onPaymentCanceled,
  });

  // ─── Response ──────────────────────────────────────────────────────────────

  return {
    isConfirmingPayment,
    isUpdatingTotal,
    embeddedPaymentElementView,
    confirmPayment,
  };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type UseGiftCardStripeIntegrationProps = {
  amount: number;
  form: GiftCardCheckoutFormReturn;
  paymentMethods: readonly PaymentMethod[];
  createPayment?: (paymentState: PaymentState) => void;
  cancelPayment?: () => void;
  clearPaymentState?: () => void;
  failPayment: () => void;
  startPolling?: (orderId: string) => void;
  requestFormSubmission: (paymentMethodId: string) => Promise<void>;
};
